import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useCreateAdvisoryNeedTask,
    useCreateLifeEvent,
    useDeleteAdvisoryNeedTask,
    useDeleteLifeEvent,
    useUpdateAdvisoryNeedTask,
    useUpdateLifeEvent,
} from '../../../api';
import type { EventType } from '../../../api/generated/client';
import { useUpdateMaturityEvent } from '../../../api/maturity-events-api';
import type { AdvisoryOrLifeEventType } from '../../../interfaces/event';
import {
    buildCreateAdvisoryNeedTask,
    buildCreateLifeEvent,
    buildUpdateAdvisoryNeedTask,
    buildUpdateLifeEvent,
    buildUpdateMaturityEvent,
    isAdvisoryEventNew,
    isLifeEvent,
    isMaturity,
} from '../../../utils/eventUtils';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';

export const useEventApiRequest = (onSuccess?: () => void) => {
    const [inProgress, setInProgress] = useState(false);
    const { t } = useTranslation();
    const createLifeEvent = useCreateLifeEvent();
    const updateLifeEvent = useUpdateLifeEvent();
    const deleteLifeEvent = useDeleteLifeEvent();

    const createAdvisoryNeedTask = useCreateAdvisoryNeedTask();
    const updateAdvisoryNeedTask = useUpdateAdvisoryNeedTask();
    const deleteAdvisoryNeedTask = useDeleteAdvisoryNeedTask();

    const updateMaturity = useUpdateMaturityEvent();

    const deleteEvent = (eventId?: number, eventType?: EventType) => {
        if (eventId != null) {
            if (eventType === 'ADVISORY_NEED_TASK') {
                deleteAdvisoryNeedTask.mutate(eventId);
            } else if (eventType === 'LIFE_EVENT') {
                deleteLifeEvent.mutate(eventId);
            }
        }
    };

    const updateEvent = (event: AdvisoryOrLifeEventType) => {
        setInProgress(true);
        if (isAdvisoryEventNew(event)) {
            updateAdvisoryNeedTask.mutate(
                { taskId: event.id!, task: buildUpdateAdvisoryNeedTask(event) },
                {
                    onSuccess: () => showSuccessToast(t('yourChangesHaveBeenSaved')),
                    onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainCouldNotEditEvent') }),
                },
            );
        } else if (isLifeEvent(event)) {
            updateLifeEvent.mutate(
                { eventId: event.id!, event: buildUpdateLifeEvent(event) },
                {
                    onSuccess: () => showSuccessToast(t('yourChangesHaveBeenSaved')),
                    onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainFailedToEditEventOnRoadmap') }),
                },
            );
        } else if (isMaturity(event)) {
            updateMaturity.mutate(buildUpdateMaturityEvent(event), {
                onSuccess: () => showSuccessToast(t('yourChangesHaveBeenSaved')),
                onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainFailedToEditEventOnRoadmap') }),
            });
        }
        setInProgress(false);
        onSuccess?.();
    };

    const createEvent = (event: AdvisoryOrLifeEventType) => {
        setInProgress(true);
        if (isAdvisoryEventNew(event)) {
            createAdvisoryNeedTask.mutate(buildCreateAdvisoryNeedTask(event), {
                onSuccess: () => showSuccessToast(t('needAddedToRoadmap')),
                onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainCouldNotAddNeedToRoadmap') }),
            });
        } else if (isLifeEvent(event)) {
            createLifeEvent.mutate(buildCreateLifeEvent(event), {
                onSuccess: () => showSuccessToast(t('successActionGeneric')),
                onError: (error) => showErrorToast({ error, userMessage: t('pleaseTryAgainFailedToAddEventToRoadmap') }),
            });
        }
        setInProgress(false);
        onSuccess?.();
    };

    return {
        createEvent,
        updateEvent,
        deleteEvent,
        inProgress,
    };
};
